import React, { useState, useEffect } from "react"
import { ChevronLeft, Settings, Check, X, RotateCcw } from "lucide-react"

// Enhanced mock data for Spanish grammar lessons with proper practice exercises
const getSpanishGrammarLessonsByLevel = level => {
	const lessons = {
		beginner: [
			{
				id: "gram-b-1",
				title: "Gender and Articles",
				description:
					"Learn about masculine and feminine nouns and how to use articles",
				concepts: [
					{
						title: "Masculine and Feminine Nouns",
						explanation:
							"In Spanish, all nouns are either masculine or feminine. Generally, nouns ending in -o are masculine, and nouns ending in -a are feminine, though there are exceptions.",
						examples: [
							{ spanish: "el libro", english: "the book (masculine)" },
							{ spanish: "la casa", english: "the house (feminine)" },
							{ spanish: "el mapa", english: "the map (masculine exception)" },
							{ spanish: "la mano", english: "the hand (feminine exception)" },
						],
					},
				],
				practice: [
					{
						type: "multiple-choice",
						question: "Which article goes with 'casa'?",
						options: ["el", "la", "los", "las"],
						answer: "la",
						explanation:
							"'Casa' is feminine, so it uses the feminine article 'la'.",
					},
					{
						type: "multiple-choice",
						question: "Which article goes with 'libro'?",
						options: ["el", "la", "los", "las"],
						answer: "el",
						explanation:
							"'Libro' is masculine, so it uses the masculine article 'el'.",
					},
					{
						type: "fill-blank",
						question: "Complete: ___ mano",
						answer: "la",
						explanation:
							"Even though 'mano' ends in -o, it's feminine and uses 'la'.",
					},
				],
			},
			{
				id: "gram-b-2",
				title: "Subject Pronouns",
				description: "Learn the personal pronouns used as subjects in Spanish",
				concepts: [
					{
						title: "Spanish Subject Pronouns",
						explanation:
							"Subject pronouns are used to replace the subject of a sentence. In Spanish, these include: yo (I), tú (you informal), él (he), ella (she), usted (you formal), nosotros/nosotras (we), vosotros/vosotras (you all informal), ellos/ellas (they), ustedes (you all formal).",
						examples: [
							{ spanish: "Yo hablo español.", english: "I speak Spanish." },
							{ spanish: "Tú comes manzanas.", english: "You eat apples." },
							{ spanish: "Ella estudia mucho.", english: "She studies a lot." },
							{ spanish: "Nosotros vivimos aquí.", english: "We live here." },
						],
					},
				],
				practice: [
					{
						type: "multiple-choice",
						question: "Which pronoun means 'we' (masculine or mixed group)?",
						options: ["nosotros", "nosotras", "vosotros", "ellos"],
						answer: "nosotros",
						explanation:
							"'Nosotros' is used for 'we' when referring to a masculine or mixed group.",
					},
					{
						type: "fill-blank",
						question: "Complete: ___ hablo español. (I speak Spanish)",
						answer: "yo",
						explanation: "'Yo' means 'I' in Spanish.",
					},
					{
						type: "matching",
						question: "Match the pronouns with their English translations:",
						pairs: [
							{ item: "tú", match: "you (informal)" },
							{ item: "ella", match: "she" },
							{ item: "ellos", match: "they (masculine)" },
						],
					},
				],
			},
			{
				id: "gram-b-3",
				title: "Present Tense Verbs",
				description:
					"Learn how to conjugate regular verbs in the present tense",
				concepts: [
					{
						title: "Regular -AR Verbs",
						explanation:
							"Most Spanish verbs end in -ar, -er, or -ir. To conjugate regular -ar verbs in the present tense, remove the -ar ending and add the appropriate ending: -o, -as, -a, -amos, -áis, -an.",
						examples: [
							{ spanish: "Yo hablo", english: "I speak" },
							{ spanish: "Tú hablas", english: "You speak" },
							{ spanish: "Él/Ella habla", english: "He/She speaks" },
							{ spanish: "Nosotros hablamos", english: "We speak" },
						],
					},
				],
				practice: [
					{
						type: "multiple-choice",
						question: "How do you conjugate 'hablar' for 'yo'?",
						options: ["hablo", "hablas", "habla", "hablamos"],
						answer: "hablo",
						explanation:
							"For 'yo' with -ar verbs, we use the ending -o, so 'hablar' becomes 'hablo'.",
					},
					{
						type: "fill-blank",
						question: "Complete: Tú _____ español. (speak)",
						answer: "hablas",
						explanation:
							"For 'tú' with -ar verbs, we use the ending -as, so it's 'hablas'.",
					},
					{
						type: "conjugation",
						question:
							"Conjugate the verb 'estudiar' (to study) for 'nosotros':",
						answer: "estudiamos",
						explanation:
							"For 'nosotros' with -ar verbs, we use -amos: estudiar → estudiamos.",
					},
				],
			},
		],
		intermediate: [
			{
				id: "gram-i-1",
				title: "Past Tenses - Preterite vs Imperfect",
				description: "Learn the preterite and imperfect past tenses",
				concepts: [
					{
						title: "Preterite vs. Imperfect",
						explanation:
							"Spanish has two main past tenses: preterite (for completed actions) and imperfect (for ongoing or habitual past actions). Preterite describes specific completed events, while imperfect describes ongoing states or repeated actions.",
						examples: [
							{
								spanish: "Comí una manzana.",
								english: "I ate an apple. (preterite - completed action)",
							},
							{
								spanish: "Comía manzanas todos los días.",
								english:
									"I used to eat apples every day. (imperfect - habitual action)",
							},
							{
								spanish: "Estudiaste anoche.",
								english: "You studied last night. (preterite - specific time)",
							},
							{
								spanish: "Estudiabas cuando llamé.",
								english:
									"You were studying when I called. (imperfect - ongoing)",
							},
						],
					},
				],
				practice: [
					{
						type: "multiple-choice",
						question: "Which sentence uses the preterite correctly?",
						options: [
							"Yo comía una pizza ayer.",
							"Yo comí una pizza ayer.",
							"Yo como una pizza ayer.",
							"Yo comeré una pizza ayer.",
						],
						answer: "Yo comí una pizza ayer.",
						explanation:
							"Preterite is used for completed actions at specific times. 'Ayer' (yesterday) indicates a completed action.",
					},
					{
						type: "fill-blank",
						question:
							"Complete with preterite: Ella _____ (estudiar) toda la noche.",
						answer: "estudió",
						explanation:
							"The preterite form of 'estudiar' for 'ella' is 'estudió'.",
					},
				],
			},
		],
		advanced: [
			{
				id: "gram-a-1",
				title: "Subjunctive Mood",
				description: "Master the subjunctive mood in Spanish",
				concepts: [
					{
						title: "When to Use the Subjunctive",
						explanation:
							"The subjunctive mood is used to express wishes, doubts, emotions, hypothetical situations, and commands. It's triggered by certain expressions and conjunctions that express uncertainty or subjectivity.",
						examples: [
							{
								spanish: "Espero que vengas.",
								english: "I hope that you come. (subjunctive)",
							},
							{
								spanish: "Sé que vienes.",
								english: "I know that you are coming. (indicative)",
							},
							{
								spanish: "Dudo que sea verdad.",
								english: "I doubt that it's true. (subjunctive)",
							},
							{
								spanish: "Es importante que estudies.",
								english: "It's important that you study. (subjunctive)",
							},
						],
					},
				],
				practice: [
					{
						type: "multiple-choice",
						question: "Which sentence requires the subjunctive?",
						options: [
							"Sé que él viene.",
							"Espero que él viene.",
							"Espero que él venga.",
							"Creo que él viene.",
						],
						answer: "Espero que él venga.",
						explanation:
							"'Esperar que' (to hope that) triggers the subjunctive, so we use 'venga' not 'viene'.",
					},
					{
						type: "fill-blank",
						question: "Complete: Es necesario que tú _____ (estudiar).",
						answer: "estudies",
						explanation:
							"'Es necesario que' requires subjunctive. The subjunctive form of 'estudiar' for 'tú' is 'estudies'.",
					},
				],
			},
		],
	}

	return lessons[level] || []
}

const SpanishGrammarPage = ({ onBackToHome, difficulty = "normal" }) => {
	const [selectedLevel, setSelectedLevel] = useState(null)
	const [selectedLesson, setSelectedLesson] = useState(null)
	const [showDifficultySelector, setShowDifficultySelector] = useState(false)
	const [currentDifficulty, setCurrentDifficulty] = useState(difficulty)

	// Practice states
	const [showPractice, setShowPractice] = useState(false)
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
	const [userAnswers, setUserAnswers] = useState({})
	const [showResults, setShowResults] = useState({})
	const [practiceComplete, setPracticeComplete] = useState(false)

	// Update local difficulty when prop changes
	useEffect(() => {
		setCurrentDifficulty(difficulty)
	}, [difficulty])

	// Reset practice state when lesson changes
	useEffect(() => {
		setShowPractice(false)
		setCurrentQuestionIndex(0)
		setUserAnswers({})
		setShowResults({})
		setPracticeComplete(false)
	}, [selectedLesson])

	// Adjust content based on difficulty
	const adjustContentByDifficulty = content => {
		if (!content) return content

		if (content.concepts) {
			return {
				...content,
				concepts: content.concepts.map(concept => {
					if (!concept.examples) return concept

					const examples = [...concept.examples]
					return {
						...concept,
						examples:
							currentDifficulty === "easy"
								? [...examples, ...examples.slice(0, 1)]
								: currentDifficulty === "hard"
								? examples.slice(0, Math.max(1, examples.length - 1))
								: examples,
					}
				}),
				practice:
					currentDifficulty === "easy"
						? [...content.practice, ...content.practice.slice(0, 1)]
						: currentDifficulty === "hard"
						? content.practice.slice(
								0,
								Math.max(1, content.practice.length - 1)
						  )
						: content.practice,
			}
		}

		return content
	}

	const handleDifficultyChange = newDifficulty => {
		setCurrentDifficulty(newDifficulty)
		setShowDifficultySelector(false)
	}

	const handleAnswerSubmit = (questionIndex, answer) => {
		setUserAnswers({ ...userAnswers, [questionIndex]: answer })
		setShowResults({ ...showResults, [questionIndex]: true })
	}

	const handleNextQuestion = () => {
		if (currentQuestionIndex < selectedLesson.practice.length - 1) {
			setCurrentQuestionIndex(currentQuestionIndex + 1)
		} else {
			setPracticeComplete(true)
		}
	}

	const handlePreviousQuestion = () => {
		if (currentQuestionIndex > 0) {
			setCurrentQuestionIndex(currentQuestionIndex - 1)
		}
	}

	const resetPractice = () => {
		setCurrentQuestionIndex(0)
		setUserAnswers({})
		setShowResults({})
		setPracticeComplete(false)
	}

	const getScore = () => {
		if (!selectedLesson?.practice) return 0
		const totalQuestions = selectedLesson.practice.length
		const correctAnswers = selectedLesson.practice.filter((question, index) => {
			const userAnswer = userAnswers[index]
			if (question.type === "matching") {
				return question.pairs.every(
					(pair, pairIndex) => userAnswer?.[pairIndex] === pair.match
				)
			}
			return userAnswer?.toLowerCase() === question.answer?.toLowerCase()
		}).length
		return Math.round((correctAnswers / totalQuestions) * 100)
	}

	const renderPracticeQuestion = (question, questionIndex) => {
		const userAnswer = userAnswers[questionIndex]
		const showResult = showResults[questionIndex]
		let isCorrect = false

		if (question.type === "matching") {
			isCorrect =
				showResult &&
				question.pairs.every(
					(pair, pairIndex) => userAnswer?.[pairIndex] === pair.match
				)
		} else {
			isCorrect =
				showResult &&
				userAnswer?.toLowerCase() === question.answer?.toLowerCase()
		}

		switch (question.type) {
			case "multiple-choice":
				return (
					<div className='bg-[#2C2C2C] p-6 rounded-lg'>
						<h4 className='text-white font-medium mb-4'>{question.question}</h4>
						<div className='space-y-3'>
							{question.options.map((option, optionIndex) => {
								let buttonClass =
									"p-3 rounded-lg cursor-pointer transition-colors text-left w-full "

								if (userAnswer === option) {
									if (showResult) {
										buttonClass += isCorrect
											? "bg-green-600 text-white"
											: "bg-red-600 text-white"
									} else {
										buttonClass += "bg-[#FF3A6E] text-white"
									}
								} else if (showResult && option === question.answer) {
									buttonClass += "bg-green-600 text-white"
								} else {
									buttonClass += "bg-[#3C3C3C] text-white hover:bg-[#4C4C4C]"
								}

								return (
									<button
										key={optionIndex}
										className={buttonClass}
										onClick={() =>
											!showResult &&
											setUserAnswers({
												...userAnswers,
												[questionIndex]: option,
											})
										}
										disabled={showResult}>
										{option}
									</button>
								)
							})}
						</div>

						{!showResult && userAnswer && (
							<button
								className='mt-4 bg-[#FF3A6E] text-white py-2 px-4 rounded-lg hover:bg-[#ff2a5e]'
								onClick={() => handleAnswerSubmit(questionIndex, userAnswer)}>
								Check Answer
							</button>
						)}

						{showResult && (
							<div
								className={`mt-4 p-4 rounded-lg ${
									isCorrect
										? "bg-green-900/30 border border-green-600"
										: "bg-red-900/30 border border-red-600"
								}`}>
								<div
									className={`font-medium flex items-center ${
										isCorrect ? "text-green-400" : "text-red-400"
									}`}>
									{isCorrect ? (
										<Check size={20} className='mr-2' />
									) : (
										<X size={20} className='mr-2' />
									)}
									{isCorrect ? "¡Correcto!" : "Incorrect"}
								</div>
								<div className='text-white mt-2'>{question.explanation}</div>
							</div>
						)}
					</div>
				)

			case "fill-blank":
				return (
					<div className='bg-[#2C2C2C] p-6 rounded-lg'>
						<h4 className='text-white font-medium mb-4'>{question.question}</h4>
						<input
							type='text'
							className='w-full p-3 bg-[#3C3C3C] text-white rounded-lg border-2 border-transparent focus:border-[#FF3A6E] outline-none'
							value={userAnswer || ""}
							onChange={e =>
								setUserAnswers({
									...userAnswers,
									[questionIndex]: e.target.value,
								})
							}
							placeholder='Type your answer here...'
							disabled={showResult}
						/>

						{!showResult && userAnswer && (
							<button
								className='mt-4 bg-[#FF3A6E] text-white py-2 px-4 rounded-lg hover:bg-[#ff2a5e]'
								onClick={() => handleAnswerSubmit(questionIndex, userAnswer)}>
								Check Answer
							</button>
						)}

						{showResult && (
							<div
								className={`mt-4 p-4 rounded-lg ${
									isCorrect
										? "bg-green-900/30 border border-green-600"
										: "bg-red-900/30 border border-red-600"
								}`}>
								<div
									className={`font-medium flex items-center ${
										isCorrect ? "text-green-400" : "text-red-400"
									}`}>
									{isCorrect ? (
										<Check size={20} className='mr-2' />
									) : (
										<X size={20} className='mr-2' />
									)}
									{isCorrect ? "¡Correcto!" : "Incorrect"}
								</div>
								{!isCorrect && (
									<div className='text-white mt-1'>
										Correct answer:{" "}
										<span className='font-semibold text-green-400'>
											{question.answer}
										</span>
									</div>
								)}
								<div className='text-white mt-2'>{question.explanation}</div>
							</div>
						)}
					</div>
				)

			case "matching":
				return (
					<div className='bg-[#2C2C2C] p-6 rounded-lg'>
						<h4 className='text-white font-medium mb-4'>Match the items:</h4>
						<div className='space-y-3'>
							{question.pairs.map((pair, pairIndex) => (
								<div key={pairIndex} className='flex items-center gap-4'>
									<div className='bg-[#3C3C3C] p-3 rounded-lg flex-1 text-white'>
										{pair.item}
									</div>
									<div className='text-[#FF3A6E]'>→</div>
									<select
										className='bg-[#3C3C3C] p-3 rounded-lg flex-1 text-white border-none outline-none'
										value={userAnswer?.[pairIndex] || ""}
										onChange={e => {
											const newAnswers = { ...userAnswer }
											newAnswers[pairIndex] = e.target.value
											setUserAnswers({
												...userAnswers,
												[questionIndex]: newAnswers,
											})
										}}
										disabled={showResult}>
										<option value=''>Select match</option>
										{question.pairs.map((p, i) => (
											<option key={i} value={p.match}>
												{p.match}
											</option>
										))}
									</select>
								</div>
							))}
						</div>

						{!showResult &&
							userAnswer &&
							Object.keys(userAnswer).length === question.pairs.length && (
								<button
									className='mt-4 bg-[#FF3A6E] text-white py-2 px-4 rounded-lg hover:bg-[#ff2a5e]'
									onClick={() => handleAnswerSubmit(questionIndex, userAnswer)}>
									Check Answers
								</button>
							)}

						{showResult && (
							<div
								className={`mt-4 p-4 rounded-lg ${
									isCorrect
										? "bg-green-900/30 border border-green-600"
										: "bg-red-900/30 border border-red-600"
								}`}>
								<div
									className={`font-medium flex items-center ${
										isCorrect ? "text-green-400" : "text-red-400"
									}`}>
									{isCorrect ? (
										<Check size={20} className='mr-2' />
									) : (
										<X size={20} className='mr-2' />
									)}
									{isCorrect
										? "Perfect matches!"
										: "Some matches were incorrect"}
								</div>
								<div className='text-white mt-2'>
									<div className='font-medium'>Correct matches:</div>
									<ul className='list-disc pl-5 mt-1'>
										{question.pairs.map((pair, i) => (
											<li key={i} className='text-sm'>
												{pair.item} → {pair.match}
											</li>
										))}
									</ul>
								</div>
							</div>
						)}
					</div>
				)

			case "conjugation":
				return (
					<div className='bg-[#2C2C2C] p-6 rounded-lg'>
						<h4 className='text-white font-medium mb-4'>{question.question}</h4>
						<input
							type='text'
							className='w-full p-3 bg-[#3C3C3C] text-white rounded-lg border-2 border-transparent focus:border-[#FF3A6E] outline-none'
							value={userAnswer || ""}
							onChange={e =>
								setUserAnswers({
									...userAnswers,
									[questionIndex]: e.target.value,
								})
							}
							placeholder='Enter the conjugated form...'
							disabled={showResult}
						/>

						{!showResult && userAnswer && (
							<button
								className='mt-4 bg-[#FF3A6E] text-white py-2 px-4 rounded-lg hover:bg-[#ff2a5e]'
								onClick={() => handleAnswerSubmit(questionIndex, userAnswer)}>
								Check Answer
							</button>
						)}

						{showResult && (
							<div
								className={`mt-4 p-4 rounded-lg ${
									isCorrect
										? "bg-green-900/30 border border-green-600"
										: "bg-red-900/30 border border-red-600"
								}`}>
								<div
									className={`font-medium flex items-center ${
										isCorrect ? "text-green-400" : "text-red-400"
									}`}>
									{isCorrect ? (
										<Check size={20} className='mr-2' />
									) : (
										<X size={20} className='mr-2' />
									)}
									{isCorrect ? "¡Excelente!" : "Not quite right"}
								</div>
								{!isCorrect && (
									<div className='text-white mt-1'>
										Correct answer:{" "}
										<span className='font-semibold text-green-400'>
											{question.answer}
										</span>
									</div>
								)}
								<div className='text-white mt-2'>{question.explanation}</div>
							</div>
						)}
					</div>
				)

			default:
				return null
		}
	}

	// Render lessons list for a selected level
	const renderLessonsList = () => {
		if (!selectedLevel) return null

		const lessons = getSpanishGrammarLessonsByLevel(selectedLevel)
		const adjustedLessons =
			currentDifficulty === "hard"
				? lessons.slice(0, Math.max(1, lessons.length - 1))
				: lessons

		return (
			<div>
				<div className='flex justify-between items-center mb-4'>
					<h2 className='text-xl font-semibold text-white capitalize'>
						{selectedLevel} Level - Spanish Grammar
					</h2>
					<div className='bg-[#2C2C2C] py-1 px-3 rounded-full flex items-center'>
						<span className='text-white text-sm capitalize'>
							{currentDifficulty}
						</span>
					</div>
				</div>

				<div className='grid grid-cols-1 gap-4 mb-6'>
					{adjustedLessons.map(lesson => (
						<button
							key={lesson.id}
							className='bg-[#2C2C2C] p-4 rounded-lg text-left hover:bg-[#3C3C3C] transition-colors'
							onClick={() =>
								setSelectedLesson(adjustContentByDifficulty(lesson))
							}>
							<div className='flex items-start'>
								<div className='bg-[#FF3A6E] h-10 w-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0'>
									<span className='text-white text-sm font-medium'>
										{lesson.id.split("-")[2]}
									</span>
								</div>
								<div>
									<h3 className='text-white font-medium'>{lesson.title}</h3>
									<p className='text-[#A0A0A0] text-sm'>{lesson.description}</p>
									<div className='mt-2 text-xs text-[#FF3A6E]'>
										{lesson.practice?.length || 0} practice questions
									</div>
								</div>
							</div>
						</button>
					))}
				</div>

				<button
					className='bg-[#1E1E1E] text-white py-3 px-4 rounded-lg flex items-center'
					onClick={() => setSelectedLevel(null)}>
					<ChevronLeft size={16} className='mr-2' />
					Back to Levels
				</button>
			</div>
		)
	}

	// Render practice completion screen
	const renderPracticeComplete = () => {
		const score = getScore()
		return (
			<div className='bg-[#1E1E1E] rounded-lg p-8 text-center'>
				<div className='text-6xl mb-4'>🎉</div>
				<h2 className='text-2xl font-bold text-white mb-2'>
					Practice Complete!
				</h2>
				<div className='text-4xl font-bold text-[#FF3A6E] mb-4'>{score}%</div>
				<p className='text-[#A0A0A0] mb-6'>
					{score >= 80
						? "Excellent work!"
						: score >= 60
						? "Good job! Keep practicing."
						: "Keep studying and try again!"}
				</p>
				<div className='flex flex-col sm:flex-row gap-3 justify-center'>
					<button
						className='bg-[#2C2C2C] text-white py-2 px-6 rounded-lg hover:bg-[#3C3C3C] flex items-center justify-center'
						onClick={() => setShowPractice(false)}>
						<ChevronLeft size={16} className='mr-2' />
						Back to Lesson
					</button>
					<button
						className='bg-[#FF3A6E] text-white py-2 px-6 rounded-lg hover:bg-[#ff2a5e] flex items-center justify-center'
						onClick={resetPractice}>
						<RotateCcw size={16} className='mr-2' />
						Try Again
					</button>
				</div>
			</div>
		)
	}

	// Render a lesson
	const renderLesson = () => {
		if (!selectedLesson) return null

		if (practiceComplete) {
			return renderPracticeComplete()
		}

		if (showPractice) {
			const currentQuestion = selectedLesson.practice[currentQuestionIndex]
			const progress =
				((currentQuestionIndex + 1) / selectedLesson.practice.length) * 100

			return (
				<div>
					<button
						className='bg-[#1E1E1E] text-white py-2 px-4 rounded-lg flex items-center mb-4'
						onClick={() => setShowPractice(false)}>
						<ChevronLeft size={16} className='mr-2' />
						Back to Lesson
					</button>

					<div className='bg-[#1E1E1E] rounded-lg p-6'>
						<div className='flex justify-between items-center mb-4'>
							<h2 className='text-xl font-bold text-white'>
								Practice: {selectedLesson.title}
							</h2>
							<div className='text-[#A0A0A0] text-sm'>
								Question {currentQuestionIndex + 1} of{" "}
								{selectedLesson.practice.length}
							</div>
						</div>

						{/* Progress bar */}
						<div className='mb-6'>
							<div className='bg-[#2C2C2C] rounded-full h-2 overflow-hidden'>
								<div
									className='bg-[#FF3A6E] h-full transition-all duration-300'
									style={{ width: `${progress}%` }}></div>
							</div>
						</div>

						{/* Current question */}
						{renderPracticeQuestion(currentQuestion, currentQuestionIndex)}

						{/* Navigation */}
						<div className='flex justify-between mt-6'>
							<button
								className='bg-[#2C2C2C] text-white py-2 px-4 rounded-lg disabled:opacity-50'
								onClick={handlePreviousQuestion}
								disabled={currentQuestionIndex === 0}>
								Previous
							</button>

							{showResults[currentQuestionIndex] && (
								<button
									className='bg-[#FF3A6E] text-white py-2 px-4 rounded-lg'
									onClick={handleNextQuestion}>
									{currentQuestionIndex === selectedLesson.practice.length - 1
										? "Finish"
										: "Next"}
								</button>
							)}
						</div>
					</div>
				</div>
			)
		}

		return (
			<div>
				<button
					className='bg-[#1E1E1E] text-white py-2 px-4 rounded-lg flex items-center mb-4'
					onClick={() => setSelectedLesson(null)}>
					<ChevronLeft size={16} className='mr-2' />
					Back to Lessons
				</button>

				<div className='bg-[#1E1E1E] rounded-lg p-6'>
					<div className='flex justify-between items-center mb-3'>
						<h2 className='text-xl font-bold text-white'>
							{selectedLesson.title}
						</h2>
						<div className='bg-[#2C2C2C] py-1 px-3 rounded-full flex items-center'>
							<span className='text-white text-sm capitalize'>
								{currentDifficulty}
							</span>
						</div>
					</div>

					{/* Lesson tabs */}
					<div className='flex mb-6'>
						<button
							className={`py-2 px-6 rounded-l-lg ${
								!showPractice
									? "bg-[#FF3A6E] text-white"
									: "bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#3C3C3C]"
							}`}
							onClick={() => setShowPractice(false)}>
							Learn
						</button>
						<button
							className={`py-2 px-6 rounded-r-lg ${
								showPractice
									? "bg-[#FF3A6E] text-white"
									: "bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#3C3C3C]"
							}`}
							onClick={() => setShowPractice(true)}>
							Practice ({selectedLesson.practice?.length || 0})
						</button>
					</div>

					{/* Lesson content */}
					{selectedLesson.concepts &&
						selectedLesson.concepts.map((concept, conceptIndex) => (
							<div key={conceptIndex} className='mb-6'>
								<h3 className='text-white font-semibold mb-3'>
									{concept.title}
								</h3>
								<p className='text-white mb-4'>
									{concept.explanation}
									{currentDifficulty === "easy" &&
										" Here are some additional explanations to help you understand better."}
									{currentDifficulty === "hard" &&
										" Let's go through this concept quickly as you're challenging yourself."}
								</p>

								{concept.examples && concept.examples.length > 0 && (
									<div className='bg-[#2C2C2C] p-4 rounded-lg'>
										<h4 className='text-white font-medium mb-2'>Examples:</h4>
										{concept.examples.map((example, index) => (
											<div key={index} className='mb-3 last:mb-0'>
												<div className='text-white font-medium'>
													{example.spanish}
												</div>
												<div className='text-[#A0A0A0] text-sm'>
													{example.english}
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						))}

					{/* Action button */}
					<div className='flex justify-center'>
						<button
							className='bg-[#FF3A6E] text-white py-3 px-8 rounded-lg hover:bg-[#ff2a5e] transition-colors'
							onClick={() => setShowPractice(true)}>
							Start Practice
						</button>
					</div>
				</div>
			</div>
		)
	}

	// If a lesson is selected, show that
	if (selectedLesson) {
		return renderLesson()
	}

	// If a level is selected, show the lessons
	if (selectedLevel) {
		return renderLessonsList()
	}

	// Default view: level selection
	return (
		<div>
			<div className='flex justify-between items-center mb-6'>
				<div className='flex items-center'>
					<button
						className='bg-[#1E1E1E] text-white p-2 rounded-lg mr-3'
						onClick={onBackToHome}>
						<ChevronLeft size={20} />
					</button>
					<h1 className='text-2xl font-bold text-white'>Spanish Grammar</h1>
				</div>

				{/* Difficulty Selector */}
				<div className='relative'>
					<button
						className='bg-[#2C2C2C] flex items-center gap-2 py-2 px-4 rounded-lg text-white hover:bg-[#3C3C3C] transition-colors'
						onClick={() => setShowDifficultySelector(!showDifficultySelector)}>
						<Settings size={16} />
						<span className='capitalize'>{currentDifficulty}</span>
					</button>

					{/* Difficulty Dropdown */}
					{showDifficultySelector && (
						<div className='absolute right-0 mt-2 bg-[#2C2C2C] rounded-lg p-2 z-10 w-48 shadow-lg'>
							{["easy", "normal", "hard"].map(level => (
								<button
									key={level}
									className={`w-full text-left p-2 rounded-lg mb-1 last:mb-0 transition-colors
                    ${
											currentDifficulty === level
												? "bg-[#FF3A6E] text-white"
												: "text-white hover:bg-[#3C3C3C]"
										}`}
									onClick={() => handleDifficultyChange(level)}>
									<div className='capitalize font-medium'>{level}</div>
									<div className='text-xs mt-1 text-[#A0A0A0]'>
										{level === "easy" && "More examples, slower pace"}
										{level === "normal" && "Standard learning pace"}
										{level === "hard" && "Fewer examples, faster pace"}
									</div>
								</button>
							))}
						</div>
					)}
				</div>
			</div>

			<p className='text-[#A0A0A0] mb-6'>
				Select a difficulty level to start learning Spanish grammar.
				{currentDifficulty !== "normal" && (
					<span className='block mt-1 text-[#FF3A6E]'>
						{currentDifficulty === "easy"
							? "Easy mode: More examples and practice questions"
							: "Hard mode: Fewer examples, more challenging"}
					</span>
				)}
			</p>

			<div className='grid grid-cols-1 gap-5 mb-6'>
				{/* Beginner Level Button */}
				<button
					className='bg-gradient-to-r from-[#55E6A5] to-[#00B4DB] p-6 rounded-lg text-left hover:scale-105 transition-transform'
					onClick={() => setSelectedLevel("beginner")}>
					<div className='flex justify-between items-center'>
						<div>
							<h2 className='text-white text-xl font-bold mb-2'>Beginner</h2>
							<p className='text-white text-opacity-80'>
								Start with the basics of Spanish grammar
							</p>
							<div className='mt-2 text-white text-opacity-60 text-sm'>
								• Gender and Articles • Subject Pronouns • Present Tense
							</div>
						</div>
						<div className='bg-white bg-opacity-20 rounded-full px-3 py-1'>
							<span className='text-white'>
								{getSpanishGrammarLessonsByLevel("beginner").length} lessons
							</span>
						</div>
					</div>
				</button>

				{/* Intermediate Level Button */}
				<button
					className='bg-gradient-to-r from-[#FF42A1] to-[#7B4AFF] p-6 rounded-lg text-left hover:scale-105 transition-transform'
					onClick={() => setSelectedLevel("intermediate")}>
					<div className='flex justify-between items-center'>
						<div>
							<h2 className='text-white text-xl font-bold mb-2'>
								Intermediate
							</h2>
							<p className='text-white text-opacity-80'>
								Expand your knowledge with more complex structures
							</p>
							<div className='mt-2 text-white text-opacity-60 text-sm'>
								• Past Tenses • Ser vs Estar • Future Tense
							</div>
						</div>
						<div className='bg-white bg-opacity-20 rounded-full px-3 py-1'>
							<span className='text-white'>
								{getSpanishGrammarLessonsByLevel("intermediate").length} lessons
							</span>
						</div>
					</div>
				</button>

				{/* Advanced Level Button */}
				<button
					className='bg-gradient-to-r from-[#FF4D4D] to-[#9B51E0] p-6 rounded-lg text-left hover:scale-105 transition-transform'
					onClick={() => setSelectedLevel("advanced")}>
					<div className='flex justify-between items-center'>
						<div>
							<h2 className='text-white text-xl font-bold mb-2'>Advanced</h2>
							<p className='text-white text-opacity-80'>
								Master complex grammar for fluent communication
							</p>
							<div className='mt-2 text-white text-opacity-60 text-sm'>
								• Subjunctive Mood • Conditional • Complex Tenses
							</div>
						</div>
						<div className='bg-white bg-opacity-20 rounded-full px-3 py-1'>
							<span className='text-white'>
								{getSpanishGrammarLessonsByLevel("advanced").length} lessons
							</span>
						</div>
					</div>
				</button>
			</div>

			{/* Grammar learning tips */}
			<div className='bg-[#1E1E1E] rounded-lg p-4 mt-6'>
				<h3 className='text-white font-semibold mb-3'>
					📚 Grammar Learning Tips
				</h3>
				<ul className='text-[#A0A0A0] text-sm space-y-2'>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>
							Read the explanations carefully before starting practice
						</span>
					</li>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>Study the examples to understand patterns</span>
					</li>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>Don't worry about mistakes - they help you learn!</span>
					</li>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>Practice regularly for better retention</span>
					</li>
				</ul>
			</div>
		</div>
	)
}

export default SpanishGrammarPage
